
import { useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { supabaseWorker } from '@/integrations/supabase/workerClient';
import { useLogger } from '@/utils/logging/logger';

// Define valid table names based on the actual Supabase schema
type ValidTableName = 
  | 'accounts'
  | 'calendar_events'
  | 'categories'
  | 'clients'
  | 'contracts'
  | 'conversations'
  | 'cross_location_orders'
  | 'custom_permissions'
  | 'delivery_options'
  | 'discount_rules'
  | 'error_logs'
  | 'expenses'
  | 'fiscal_equipment_config'
  | 'inventory'
  | 'inventory_transfer_items'
  | 'inventory_transfers'
  | 'locations'
  | 'messages'
  | 'notifications'
  | 'orders'
  | 'passport_departments'
  | 'payment_methods'
  | 'performance_logs'
  | 'rental_kits'
  | 'rentals'
  | 'repairs'
  | 'services'
  | 'store_products'
  | 'tags'
  | 'tasks'
  | 'user_roles'
  | 'sales'
  | 'sale_items';

type CreateFn = (data: Record<string, unknown>) => Promise<unknown>;
type UpdateFn = (id: string, updates: Record<string, unknown>) => Promise<unknown>;
type DeleteFn = (id: string) => Promise<void>;

export const useEntityOperations = (tableName: ValidTableName, entityDisplayName?: string): {
  create: CreateFn;
  update: UpdateFn;
  delete: DeleteFn;
  loading: boolean;
} => {
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const logger = useLogger('useEntityOperations');

  const create: CreateFn = async (data: Record<string, unknown>): Promise<unknown> => {
    setLoading(true);
    try {
      const { data: result, error } = await supabaseWorker
        .from(tableName)
        .insert(data)
        .select()
        .single();

      if (error) throw error;

      toast({
        title: "Успех",
        description: `${entityDisplayName || 'Запись'} успешно создана`,
      });

      return result as unknown;
    } catch (error: unknown) {
      const e = error as { message?: string } | undefined;
      logger.error(`Error creating ${entityDisplayName || tableName}`, error);
      toast({
        title: "Ошибка",
        description: e?.message || `Не удалось создать ${entityDisplayName?.toLowerCase() || 'запись'}`,
        variant: "destructive",
      });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const update: UpdateFn = async (id: string, updates: Record<string, unknown>): Promise<unknown> => {
    setLoading(true);
    try {
      if (!id) {
        throw new Error('Update failed: missing entity id');
      }
      const { data: result, error } = await supabaseWorker
        .from(tableName)
        .update(updates)
        .eq('id', id)
        .select()
        .maybeSingle();

      if (error) throw error;
      if (!result) {
        throw new Error(`Update failed: no row returned for id ${id}`);
      }

      toast({
        title: "Успех",
        description: `${entityDisplayName || 'Запись'} успешно обновлена`,
      });

      return result as unknown;
    } catch (error: unknown) {
      const e = error as { message?: string } | undefined;
      logger.error(`Error updating ${entityDisplayName || tableName}`, error);
      toast({
        title: "Ошибка",
        description: e?.message || `Не удалось обновить ${entityDisplayName?.toLowerCase() || 'запись'}`,
        variant: "destructive",
      });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const deleteEntity: DeleteFn = async (id: string): Promise<void> => {
    logger.info('Starting deletion', { tableName, id });
    setLoading(true);
    try {
      const { error } = await supabaseWorker
        .from(tableName)
        .delete()
        .eq('id', id);

      logger.debug('Delete result', { tableName, error });

      if (error) {
        const e = error as { message?: string; code?: string; details?: string; hint?: string };
        logger.error('Delete error', { tableName, message: e?.message, code: e?.code, details: e?.details, hint: e?.hint });
        throw error;
      }

      logger.info('Successfully deleted', { tableName, id });
      toast({
        title: "Успех",
        description: `${entityDisplayName || 'Запись'} успешно удалена`,
      });
    } catch (error: unknown) {
      const e = error as { message?: string } | undefined;
      logger.error(`Error deleting ${entityDisplayName || tableName}`, error);
      toast({
        title: "Ошибка",
        description: e?.message || `Не удалось удалить ${entityDisplayName?.toLowerCase() || 'запись'}`,
        variant: "destructive",
      });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    create,
    update,
    delete: deleteEntity,
    loading
  };
};
